/**
 * Состав комплекта и результат проверки по каждой позиции (S29).
 *
 * ## Зачем таблица появилась
 *
 * ADR-0016 свёл вкладку к списку ошибок — и это было верно ровно до первого
 * чистого комплекта. Заказчик прошёл стенд на 83 страницах и сказал прямо:
 * «результат распознавания невозможно понять». Экран, отвечающий только на
 * вопрос «что не так», при отсутствии ошибок показывает пустую таблицу, по
 * которой нельзя понять даже, прочитал ли портал хоть что-нибудь.
 *
 * Порядок разделов назвал заказчик: **акт → строки внутреннего реестра →
 * паспорта, сертификаты и прочее**, с подтверждением в каждой строке.
 *
 * ## Это НЕ возврат раздела «Документы комплекта»
 *
 * ADR-0016 убрал не показ состава, а требование СОБИРАТЬ его руками. Здесь нет
 * ни одной кнопки, меняющей состав: границы документов подтверждает конвейер,
 * нарезка идёт сама, и просить пользователя что-либо подтверждать таблица не
 * начинает.
 *
 * ## Всё готовым приходит с сервера
 *
 * Заголовок строки, номер страницы, состояние сверки с реестром и вердикты
 * правил живут только в БД (ADR-0016, решение 2). Клиент печатает пришедшее и
 * не пересортировывает: вторая сортировка либо повторила бы серверную, либо
 * разошлась бы с ней.
 *
 * ## Комплекты — отдельными группами (S44)
 *
 * Загружают папку ИД: опись передачи и двенадцать актов, у каждого свои
 * приложения. Плоская таблица на 134 строки отвечала на вопрос «что в папке», но
 * не на вопрос «чего не хватает вот этому акту» — а спрашивают именно его.
 * Порядок групп: комплекты по номеру акта, затем «Вне комплектов» (опись,
 * титулы), затем замечания без адреса.
 *
 * ## Блоки сворачиваются (S59)
 *
 * Папка на двенадцать актов — это двенадцать групп по четыре таблицы, и до
 * нужной приходилось прокручивать всё. Заголовок комплекта и заголовок секции
 * сворачивают своё содержимое; свёрнутое запоминается в браузере на папку
 * (`collapse-state.ts`). ADR-0016 это не нарушает: сворачивается ПОКАЗ, ни одной
 * кнопки, меняющей состав, здесь по-прежнему нет.
 *
 * ## Контракт `data-testid`
 *
 * `checks-report` · `checks-report-group-{kind}` · `checks-report-group-{kind}-header` ·
 * `checks-report-section-{kind}` · `checks-report-section-{kind}-header` ·
 * `checks-report-row-{id}`
 */
import { useState, type ReactNode } from 'react';
import { Collapse, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import type { CheckReport, ReportGroup, ReportRow, ReportSection } from '../../api/types.js';
import { Link } from '../../app/router.js';
import { ToneTag } from '../../shared/tags.js';
import { readCollapsedKeys, storeCollapsedKeys, toggleCollapsedKey } from './collapse-state.js';
import {
  candidatePagesOf,
  datesLabel,
  itemLabel,
  markOf,
  pageHref,
  pagesLabel,
  rowDetailText,
  rowHref,
  rowTagText,
  sectionTally,
  toneOf,
} from './report.js';

/** Что свёрнуто и как переключить. Одно состояние на весь отчёт. */
interface CollapseState {
  readonly collapsed: readonly string[];
  readonly toggle: (key: string) => void;
}

export function ReportTable({
  folderId,
  report,
}: {
  folderId: string;
  report: CheckReport;
}): ReactNode {
  if (report.groups.length === 0) {
    return (
      <Typography.Paragraph type="secondary" data-testid="checks-report">
        Портал ещё не разобрал папку на документы: состав появится после распознавания.
      </Typography.Paragraph>
    );
  }

  return <ReportBody folderId={folderId} report={report} />;
}

/**
 * Тело отчёта со своим состоянием свёрнутых блоков.
 *
 * Отдельный компонент, а не `useState` в `ReportTable`: у пустого отчёта
 * состояние не нужно, а хук нельзя вызывать после раннего `return`.
 */
function ReportBody({ folderId, report }: { folderId: string; report: CheckReport }): ReactNode {
  const [collapsed, setCollapsed] = useState<readonly string[]>(() => readCollapsedKeys(folderId));
  const toggle = (key: string): void => {
    setCollapsed((previous) => {
      const next = toggleCollapsedKey(previous, key);
      storeCollapsedKeys(folderId, next);
      return next;
    });
  };
  const state: CollapseState = { collapsed, toggle };

  return (
    <div data-testid="checks-report">
      {report.groups.map((group, index) => (
        <GroupBlock
          key={group.complectId ?? `${group.kind}-${String(index)}`}
          folderId={folderId}
          group={group}
          groupKey={`group:${group.complectId ?? `${group.kind}-${String(index)}`}`}
          state={state}
        />
      ))}
    </div>
  );
}

/**
 * Блок одного комплекта.
 *
 * Заголовок — то, чем инженер комплект называет: «Акт освидетельствования
 * № 48-ОТ/-1 от 10.04.2026». Приходит с сервера готовым: номер и дату акта
 * пишет в комплект барьер извлечения, и второе место, собирающее тот же
 * заголовок, разошлось бы с первым.
 */
function GroupBlock({
  folderId,
  group,
  groupKey,
  state,
}: {
  folderId: string;
  group: ReportGroup;
  groupKey: string;
  state: CollapseState;
}): ReactNode {
  const open = !state.collapsed.includes(groupKey);

  return (
    <section
      data-testid={`checks-report-group-${group.kind}`}
      style={{ marginBottom: 24 }}
      aria-label={group.title}
    >
      <Collapse
        ghost
        activeKey={open ? [groupKey] : []}
        onChange={() => {
          state.toggle(groupKey);
        }}
        items={[
          {
            key: groupKey,
            label: (
              <Typography.Title
                level={2}
                style={{ fontSize: 18, margin: 0 }}
                data-testid={`checks-report-group-${group.kind}-header`}
              >
                {group.title}
              </Typography.Title>
            ),
            children: group.sections.map((section) => (
              <SectionBlock
                key={section.kind}
                folderId={folderId}
                section={section}
                sectionKey={`${groupKey}:${section.kind}`}
                state={state}
              />
            )),
          },
        ]}
      />
    </section>
  );
}

function SectionBlock({
  folderId,
  section,
  sectionKey,
  state,
}: {
  folderId: string;
  section: ReportSection;
  sectionKey: string;
  state: CollapseState;
}): ReactNode {
  const tally = sectionTally(section);
  const open = !state.collapsed.includes(sectionKey);

  const header = (
    <Space align="baseline" wrap data-testid={`checks-report-section-${section.kind}-header`}>
      <Typography.Title level={3} style={{ fontSize: 16, margin: 0 }}>
        {section.title}
      </Typography.Title>
      {tally !== null && <Typography.Text type="secondary">{tally}</Typography.Text>}
    </Space>
  );

  return (
    <div data-testid={`checks-report-section-${section.kind}`} style={{ marginBottom: 16 }}>
      <Collapse
        ghost
        size="small"
        activeKey={open ? [sectionKey] : []}
        onChange={() => {
          state.toggle(sectionKey);
        }}
        items={[{ key: sectionKey, label: header, children: <SectionBody /> }]}
      />
    </div>
  );

  function SectionBody(): ReactNode {
    return (
      <>
        {/*
        Заявление о секции целиком — «реестр приложений в комплекте не найден».
        Это не замечание: подрядчику нечего с ним сделать кнопкой, но пустая
        секция без объяснения неотличима от сломанного экрана.
      */}
        {section.note !== null && (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {section.note}
          </Typography.Paragraph>
        )}

        {section.rows.length > 0 && <SectionTable folderId={folderId} section={section} />}
      </>
    );
  }
}

function SectionTable({
  folderId,
  section,
}: {
  folderId: string;
  section: ReportSection;
}): ReactNode {
  return (
    <>
      {section.rows.length > 0 && (
        <Table<ReportRow>
          rowKey="id"
          size="middle"
          pagination={false}
          dataSource={section.rows}
          /*
            Ширины столбцов обязаны быть ЗАКОНОМ, а не пожеланием.

            При раскладке `auto` (умолчание antd) браузер раздаёт ширину по
            содержимому, и таблица с длинным замечанием сжимала «Позицию
            комплекта» до одной буквы в строке, а соседняя таблица на той же
            странице раскладывалась совсем иначе. Разная разметка у таблиц
            одного экрана читается как разные сущности, хотя это один и тот же
            список в четырёх разрезах.
          */
          tableLayout="fixed"
          expandable={{
            // Раскрытие только там, где есть что раскрыть: пустая стрелка у
            // каждой строки обещает подробности, которых нет.
            rowExpandable: (row) => row.items.length > 0,
            expandedRowRender: (row) => <ItemList row={row} />,
            // Место под стрелку резервируется всегда: без этого таблица с
            // раскрытием и таблица без него разъезжаются по всем колонкам.
            columnWidth: 48,
          }}
          columns={reportColumns(folderId)}
        />
      )}
    </>
  );
}

/**
 * Столбцы, ОДНИ на все разделы отчёта.
 *
 * Объявлены один раз и переиспользуются каждой секцией: пока определения
 * лежали внутри `map`, ничто не мешало им разъехаться, и разъезжались они уже
 * при раскладке `auto`. Ширины — в процентах, чтобы таблица дышала вместе с
 * окном; «Стр.» в пикселях, потому что номер страницы шире не становится.
 */
function reportColumns(folderId: string): ColumnsType<ReportRow> {
  return [
    {
      title: 'Стр.',
      key: 'pages',
      width: 72,
      render: (_value, row) => <PagesCell folderId={folderId} row={row} />,
    },
    {
      title: 'Позиция комплекта',
      key: 'title',
      width: '34%',
      render: (_value, row) => (
        /*
          Метка на содержимом ячейки, а не через `onRow`: прокидывает ли antd
          произвольные `data-*` до узла строки — её внутреннее дело, и
          завязывать на это контракт прогонов значит получить отказ сценария на
          обновлении библиотеки, а не на изменении портала (тот же довод, что
          был у таблицы замечаний).
        */
        <Space direction="vertical" size={0} data-testid={`checks-report-row-${row.id}`}>
          <Typography.Text>{row.title}</Typography.Text>
          {row.subtitle !== null && (
            <Typography.Text type="secondary">{row.subtitle}</Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Даты',
      key: 'dates',
      width: '18%',
      render: (_value, row) => {
        const dates = datesLabel(row);
        return dates === null ? null : <Typography.Text>{dates}</Typography.Text>;
      },
    },
    {
      title: 'Результат',
      key: 'status',
      render: (_value, row) => {
        const detail = rowDetailText(row);
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {/*
              На метке — короткое слово, под ней — подробности обычным текстом.
              Метка не переносится по словам и, приняв в себя всё замечание,
              продавливала ширину соседних колонок.
            */}
            <ToneTag tone={toneOf(row.status)}>
              {markOf(row.status)} {rowTagText(row)}
            </ToneTag>
            {detail !== null && <Typography.Text>{detail}</Typography.Text>}
            {/*
              Способ устранения — сразу, а не в раскрытии строки: замечание без
              него бесполезно подрядчику (§9.1), а раскрытие — ещё одно нажатие
              ради двух строк текста.
            */}
            {row.statusHint !== null && (
              <Typography.Text type="secondary">{row.statusHint}</Typography.Text>
            )}
          </Space>
        );
      },
    },
  ];
}

/**
 * Пункты чек-листа акта либо замечания документа.
 *
 * Троичность §0.5 остаётся видимой: «неприменимо» и «не исполнялось» стоят
 * своими словами рядом с пройденным, а не прячутся и не сливаются с ним.
 * Спрятать их значило бы объявить чек-лист полным там, где часть правил вообще
 * не работала.
 */
function ItemList({ row }: { row: ReportRow }): ReactNode {
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {row.items.map((item) => (
        <Space key={item.code} size={8} align="start" wrap>
          <ToneTag tone={toneOf(item.status)}>
            {markOf(item.status)} {itemLabel(item.status)}
          </ToneTag>
          <Typography.Text>{item.title}</Typography.Text>
          {item.detail !== null && (
            <Typography.Text type="secondary">— {item.detail}</Typography.Text>
          )}
          {item.hint !== null && <Typography.Text type="secondary">{item.hint}</Typography.Text>}
          {/* Код правила нужен поддержке и не стоит ширины колонки. */}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {item.code}
          </Typography.Text>
        </Space>
      ))}
    </Space>
  );
}

/**
 * Номер страницы со ссылкой на разметку.
 *
 * Когда рабочий документ не собран, ссылки нет вовсе: неработающая ссылка хуже
 * её отсутствия, потому что по ней нажмут.
 */
function PagesCell({ folderId, row }: { folderId: string; row: ReportRow }): ReactNode {
  const label = pagesLabel(row);
  if (label === null) {
    /*
      Своей страницы нет — печатаются страницы ПОХОЖИХ документов (S59). У
      строки реестра в состоянии `candidate`/`ambiguous` документ не подтверждён,
      и колонка стояла пустой, хотя подпись говорила «похоже на стр. 19, 20».
      Каждая страница — своя ссылка; подпись говорит, что это лишь кандидат.
    */
    const candidates = candidatePagesOf(row);
    if (candidates.length === 0) return null;
    return (
      <Space size={4} wrap>
        {candidates.map((page) => {
          const href = pageHref(folderId, page);
          const text = String(page.number);
          return href === null ? (
            <Typography.Text key={page.number} type="secondary" title="страница похожего документа">
              {text}
            </Typography.Text>
          ) : (
            <Link key={page.number} to={href} title="страница похожего документа">
              {text}
            </Link>
          );
        })}
      </Space>
    );
  }
  const href = rowHref(folderId, row);
  if (href === null) return <Typography.Text>{label}</Typography.Text>;

  const link = <Link to={href}>{label}</Link>;
  // Метка по коду правила сохраняет прежний контракт прогонов: §16 проверяет
  // переход «замечание → доказательство» именно по нему, и у строк без
  // замечания её нет вовсе — там и переходить не к чему.
  return row.statusRuleCode === null ? (
    link
  ) : (
    <span data-testid={`evidence-${row.statusRuleCode}`}>{link}</span>
  );
}
